import { useDispatch, useSelector } from 'react-redux'
import type { AppState, AppDispatch } from './store'

/**
 * useAppDispatch is an alias for useDispatch, but it returns an object of type AppDispatch.
 * AppDispatch is also a function, which is used to update the store. This data type
 * is aware of the middleware added by configureStore, most importantly Thunk.
 */
export const useAppDispatch: (() => AppDispatch) = useDispatch;

/**
 * Wraps useSelector. Is aware of this app's AppState data type,
 * and also uses a comparison function, so if the selector returns an object or array,
 * but its properties/elements are identical to the old one,
 * it is considered identical.
 * 
 * Note that the comparison is shallow.
 */
export function useAppSelector<T>(selector: (state: AppState) => T)
{
    return useSelector(selector, function(oldState, newState)
    {
        const t1 = typeof oldState;
        const t2 = typeof newState;

        if (t1 !== t2) return false;
        if (oldState == null && newState == null) return true;

        if (t1 !== 'object')
        {
            return oldState === newState;
        }

        for (const key in {...oldState, ...newState})
        {
            const v1 = oldState[key];
            const v2 = newState[key];
            if (v1 !== v2)
            {
                return false;
            }
        }

        return true;
    });
}